import React, { useCallback, useEffect, useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Switch,
  Stack,
  Tab,
  Tabs,
  Tooltip,
  Typography
} from '@mui/material';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import HelpIcon from '../help/HelpIcon';
import ColumnsTab from './components/ColumnsTab';
import JoinsTab from './components/JoinsTab';
import MetadataOverridePreviewDialog from './components/MetadataOverridePreviewDialog';
import MetadataSection from './components/MetadataSection';
import SoftDeleteSection from './components/SoftDeleteSection';
import ImportTablesWizard from './components/ImportTablesWizard';
import useMetadataGenerationJob, {
  AI_UNAVAILABLE_MESSAGE,
  aiGenerationFailed
} from '../../hooks/useMetadataGenerationJob/useMetadataGenerationJob';
import { AI_COLUMN_FIELDS, AI_FIELD_TO_FORM_KEY, AI_LIST_FIELDS } from './constants/aiFieldGroups';

const labelColor = { color: 'var(--wb-text-label)' };
const accentColor = 'var(--wb-accent)';
const switchSx = {
  '& .MuiSwitch-switchBase': { color: 'var(--wb-accent)' },
  '& .MuiSwitch-switchBase.Mui-checked': { color: 'var(--wb-accent)' },
  '& .MuiSwitch-switchBase.Mui-checked + .MuiSwitch-track': { backgroundColor: 'var(--wb-x-bg-38bdf8a6, rgba(56, 189, 248, 0.65))' },
  '& .MuiSwitch-track': { backgroundColor: 'var(--wb-blue-40)' }
};

const mapConnectionTypeToDialect = (type) => {
  if (!type) {
    return '';
  }
  const normalized = String(type).toLowerCase();
  if (normalized.includes('snow')) {
    return 'snowflake';
  }
  if (normalized.includes('post')) {
    return 'postgres';
  }
  if (normalized.includes('mysql')) {
    return 'mysql';
  }
  if (normalized.includes('sql') || normalized.includes('mssql')) {
    return 'mssql';
  }
  if (normalized.includes('oracle')) {
    return 'oracle';
  }
  return '';
};

const createDefaultColumn = () => ({
  column_id: '',
  column_name: '',
  business_name: '',
  description: '',
  data_type: '',
  semantic_role: '',
  semantic_type: '',
  is_primary_key: false,
  is_foreign_key: false,
  references: '',
  nullable: true,
  required: false,
  sensitive: false,
  pattern: '',
  unit: '',
  aggregation: '',
  temporal_behavior: '',
  time_grain: '',
  domain: '',
  synonymsText: '',
  exampleValuesText: '',
  usageNotesText: '',
  usageConstraintsText: ''
});

const createDefaultJoinCondition = () => ({
  source_column: '',
  target_column: ''
});

const createDefaultJoin = () => ({
  join_id: '',
  target_table: '',
  target_schema: '',
  target_database: '',
  type: 'left',
  on: [createDefaultJoinCondition()],
  cardinality: '',
  purpose: ''
});

const defaultFormValues = {
  semantic_database_id: '',
  account_id: '',
  connection_id: '',
  catalog: '',
  database: '',
  schema: '',
  table_name: '',
  display_name: '',
  description: '',
  sql_dialect: '',
  tags: [],
  tagInput: '',
  columns: [createDefaultColumn()],
  joins: [createDefaultJoin()],
  is_active: true,
  softDelete: {
    deleted: false,
    deleted_at: ''
  }
};

const parseList = (text) => {
  if (!text) {
    return [];
  }
  return String(text)
    .split(/[,;\n]+/)
    .map((entry) => entry.trim())
    .filter(Boolean);
};

const buildColumnsPayload = (columns) =>
  columns
    .map((column) => {
      const columnName = column.column_name?.trim();
      if (!columnName) {
        return null;
      }

      const synonyms = parseList(column.synonymsText);
      const exampleValues = parseList(column.exampleValuesText);
      const usageNotes = parseList(column.usageNotesText);
      const usageConstraints = parseList(column.usageConstraintsText);

      const businessRules = usageConstraints.length
        ? { usage_constraints: usageConstraints }
        : undefined;

      const payloadColumn = {
        column_name: columnName,
        business_name: column.business_name.trim() || undefined,
        description: column.description.trim() || undefined,
        data_type: column.data_type.trim() || undefined,
        semantic_role: column.semantic_role.trim() || undefined,
        semantic_type: column.semantic_type.trim() || undefined,
        is_primary_key: !!column.is_primary_key,
        is_foreign_key: !!column.is_foreign_key,
        references: column.references.trim() || undefined,
        nullable: !!column.nullable,
        required: !!column.required,
        sensitive: !!column.sensitive,
        pattern: column.pattern.trim() || undefined,
        unit: column.unit.trim() || undefined,
        aggregation: column.aggregation.trim() || undefined,
        temporal_behavior: column.temporal_behavior.trim() || undefined,
        time_grain: column.time_grain.trim() || undefined,
        domain: column.domain.trim() || undefined
      };

      if (column.column_id) {
        payloadColumn.column_id = column.column_id;
      }

      if (synonyms.length) {
        payloadColumn.synonyms = synonyms;
      }

      if (exampleValues.length) {
        payloadColumn.example_values = exampleValues;
      }

      if (usageNotes.length) {
        payloadColumn.usage_notes = usageNotes;
      }

      if (businessRules) {
        payloadColumn.business_rules = businessRules;
      }

      return payloadColumn;
    })
    .filter(Boolean);

const isEmptyValue = (value) => value === undefined || value === null || String(value).trim() === '';

/**
 * Autocompletado de HUECOS en creación (ver handleSubmit): a diferencia de
 * MetadataOverridePreviewDialog (edición, opt-in por checkbox), acá nunca
 * se pisa un campo que el usuario ya tipeó -- solo se rellena lo que está
 * vacío, campo por campo, a nivel tabla y a nivel columna (matcheando por
 * column_name). Devuelve un formValues nuevo, no muta `current`.
 */
const computeMergedFormValues = (current, metadata) => {
  if (!metadata) {
    return current;
  }

  const aiColumnsByName = new Map(
    (Array.isArray(metadata.columns) ? metadata.columns : [])
      .map((col) => [String(col.column_name || '').trim().toLowerCase(), col])
  );

  const nextColumns = current.columns.map((column) => {
    const key = String(column.column_name || '').trim().toLowerCase();
    const aiColumn = key ? aiColumnsByName.get(key) : null;
    if (!aiColumn) {
      return column;
    }

    const merged = { ...column };
    AI_COLUMN_FIELDS.forEach((aiField) => {
      const formKey = AI_FIELD_TO_FORM_KEY[aiField];
      if (!isEmptyValue(merged[formKey])) {
        return;
      }
      const aiValue = aiColumn[aiField];
      if (AI_LIST_FIELDS.has(aiField)) {
        if (Array.isArray(aiValue) && aiValue.length) {
          merged[formKey] = aiValue.filter(Boolean).join(', ');
        }
      } else if (!isEmptyValue(aiValue)) {
        merged[formKey] = String(aiValue).trim();
      }
    });

    return merged;
  });

  return {
    ...current,
    display_name: isEmptyValue(current.display_name)
      ? (metadata.display_name || current.display_name)
      : current.display_name,
    description: isEmptyValue(current.description)
      ? (metadata.description || current.description)
      : current.description,
    columns: nextColumns
  };
};

/** true si a la tabla o a alguna de sus columnas (con nombre) le falta algún campo de negocio generable por IA. */
const hasMissingBusinessFields = (formValues) => {
  if (isEmptyValue(formValues.display_name) || isEmptyValue(formValues.description)) {
    return true;
  }
  return formValues.columns.some((column) => {
    if (isEmptyValue(column.column_name)) {
      return false;
    }
    return AI_COLUMN_FIELDS.some((aiField) => isEmptyValue(column[AI_FIELD_TO_FORM_KEY[aiField]]));
  });
};

const buildJoinsPayload = (joins) =>
  joins
    .map((join) => {
      const targetTable = join.target_table.trim();
      const sanitizedConditions = (join.on || [])
        .map((condition) => ({
          source_column: condition.source_column.trim(),
          target_column: condition.target_column.trim()
        }))
        .filter((condition) => condition.source_column && condition.target_column);

      if (!targetTable && sanitizedConditions.length === 0) {
        return null;
      }

      const payloadJoin = {
        target_table: targetTable,
        target_schema: join.target_schema.trim() || undefined,
        target_database: join.target_database.trim() || undefined,
        type: join.type || 'left',
        on: sanitizedConditions
      };

      if (join.join_id) {
        payloadJoin.join_id = join.join_id;
      }

      if (join.cardinality) {
        payloadJoin.cardinality = join.cardinality;
      }

      if (join.purpose) {
        payloadJoin.purpose = join.purpose.trim() || undefined;
      }

      return payloadJoin;
    })
    .filter(Boolean);

const buildSubmitPayload = (values, resolvedAccountId) => {
  const columnsPayload = buildColumnsPayload(values.columns);
  const joinsPayload = buildJoinsPayload(values.joins);

  return {
    account_id: resolvedAccountId,
    semantic_database_id: values.semantic_database_id,
    connection_id: values.connection_id,
    catalog: values.catalog.trim() || undefined,
    database: values.database.trim(),
    schema: values.schema.trim(),
    table_name: values.table_name.trim(),
    display_name: values.display_name.trim() || undefined,
    description: values.description.trim() || undefined,
    sql_dialect: values.sql_dialect || undefined,
    tags: values.tags,
    columns: columnsPayload,
    joins: joinsPayload.length ? joinsPayload : undefined,
    is_active: values.is_active,
    soft_delete: values.softDelete.deleted
      ? { deleted: true, deleted_at: values.softDelete.deleted_at || null }
      : { deleted: false }
  };
};

/** Precondición mínima para poder disparar el job de IA (mismo criterio que runMetadataGeneration). */
const canRunMetadataGeneration = (values) =>
  !!values.table_name.trim() && values.columns.some((column) => column.column_name && column.column_name.trim());

const SemanticTableFormDialog = ({
  open,
  mode = 'create',
  initialValues,
  databases = [],
  connections = [],
  onClose,
  onSubmit,
  isSaving,
  onRefreshDatabases,
  onRefreshConnections,
  isRefreshingDatabases = false,
  isRefreshingConnections = false,
  accountInfo,
  onBulkImportComplete
}) => {
  const [formValues, setFormValues] = useState(defaultFormValues);
  const [errors, setErrors] = useState({});
  const [columnErrors, setColumnErrors] = useState([]);
  const [activeTab, setActiveTab] = useState(0);
  const [importWizardOpen, setImportWizardOpen] = useState(false);
  const [aiProposal, setAiProposal] = useState(null);
  // Switch de creación: decisión explícita del usuario, apagado por
  // defecto -- controla únicamente el auto-disparo del job de IA al
  // apretar "Crear" (ver handleSubmit). El botón manual "Generar con IA"
  // no depende de esto, sigue disponible siempre.
  const [autoFillWithAI, setAutoFillWithAI] = useState(false);
  const {
    run: runMetadataGenerationJob,
    isRunning: isGeneratingAI,
    progressMessage: aiProgressMessage,
    error: aiError,
    clearError: clearAiError,
    reportError: reportAiError,
    reset: resetAiJob
  } = useMetadataGenerationJob();

  const resolvedAccountId = accountInfo?.id || formValues.account_id || '';
  const accountName = accountInfo?.name || accountInfo?.company_name || '';

  const databaseOptions = useMemo(() => databases || [], [databases]);
  const connectionOptions = useMemo(() => connections || [], [connections]);

  const connectionIndex = useMemo(() => {
    const index = {};
    connectionOptions.forEach((connection) => {
      const key =
        connection?.connection_id ||
        connection?.connectionId ||
        connection?.id ||
        connection?.ID;
      if (key) {
        index[key] = connection;
      }
    });
    return index;
  }, [connectionOptions]);

  const selectedConnection = formValues.connection_id
    ? connectionIndex[formValues.connection_id]
    : null;

  useEffect(() => {
    if (!open) {
      setFormValues(defaultFormValues);
      setErrors({});
      setColumnErrors([]);
      setActiveTab(0);
      resetAiJob();
      setAiProposal(null);
      setAutoFillWithAI(false);
      return;
    }

    const mapColumns = (columns) => {
      if (!Array.isArray(columns) || columns.length === 0) {
        return [createDefaultColumn()];
      }
      return columns.map((column) => ({
        column_id: column?.column_id || column?.columns_id || column?.COLUMN_ID || '',
        column_name: column?.column_name || column?.COLUMN_NAME || '',
        business_name: column?.business_name || column?.BUSINESS_NAME || '',
        description: column?.description || column?.DESCRIPTION || '',
        data_type: column?.data_type || column?.DATA_TYPE || '',
        semantic_role: column?.semantic_role || column?.SEMANTIC_ROLE || '',
        semantic_type: column?.semantic_type || column?.SEMANTIC_TYPE || '',
        is_primary_key: !!(column?.is_primary_key ?? column?.IS_PRIMARY_KEY),
        is_foreign_key: !!(column?.is_foreign_key ?? column?.IS_FOREIGN_KEY),
        references: column?.references || column?.REFERENCES || '',
        nullable: column?.nullable !== false && column?.NULLABLE !== false,
        required: !!(column?.required ?? column?.REQUIRED),
        sensitive: !!(column?.sensitive ?? column?.SENSITIVE),
        pattern: column?.pattern || column?.PATTERN || '',
        unit: column?.unit || column?.UNIT || '',
        aggregation: column?.aggregation || column?.AGGREGATION || '',
        temporal_behavior: column?.temporal_behavior || column?.TEMPORAL_BEHAVIOR || '',
        time_grain: column?.time_grain || column?.TIME_GRAIN || '',
        domain: column?.domain || column?.DOMAIN || '',
        synonymsText: Array.isArray(column?.synonyms) ? column.synonyms.join(', ') : '',
        exampleValuesText: Array.isArray(column?.example_values) ? column.example_values.join(', ') : '',
        usageNotesText: Array.isArray(column?.usage_notes) ? column.usage_notes.join(', ') : '',
        usageConstraintsText: Array.isArray(column?.business_rules?.usage_constraints)
          ? column.business_rules.usage_constraints.join(', ')
          : ''
      }));
    };

    const mapJoins = (joins) => {
      if (!Array.isArray(joins) || joins.length === 0) {
        return [createDefaultJoin()];
      }
      return joins.map((join) => ({
        join_id: join?.join_id || join?.JOIN_ID || '',
        target_table: join?.target_table || join?.TARGET_TABLE || '',
        target_schema: join?.target_schema || join?.TARGET_SCHEMA || '',
        target_database: join?.target_database || join?.TARGET_DATABASE || '',
        type: join?.type || join?.TYPE || 'left',
        on: Array.isArray(join?.on) && join.on.length > 0
          ? join.on.map((pair) => ({
              source_column: pair?.source_column || pair?.SOURCE_COLUMN || '',
              target_column: pair?.target_column || pair?.TARGET_COLUMN || ''
            }))
          : [createDefaultJoinCondition()],
        cardinality: join?.cardinality || join?.CARDINALITY || '',
        purpose: join?.purpose || join?.PURPOSE || ''
      }));
    };

    if (initialValues) {
      const mappedColumns = mapColumns(initialValues.columns);
      const mappedJoins = mapJoins(initialValues.joins);

      setFormValues({
        semantic_database_id:
          initialValues.semantic_database_id ||
          initialValues.semanticDatabaseId ||
          initialValues.SEMANTIC_DATABASE_ID ||
          '',
        account_id:
          initialValues.account_id ||
          initialValues.accountId ||
          initialValues.ACCOUNT_ID ||
          accountInfo?.id ||
          '',
        connection_id:
          initialValues.connection_id ||
          initialValues.connectionId ||
          initialValues.CONNECTION_ID ||
          '',
        catalog: initialValues.catalog || initialValues.CATALOG || '',
        database: initialValues.database || initialValues.DATABASE || '',
        schema: initialValues.schema || initialValues.SCHEMA || '',
        table_name:
          initialValues.table_name ||
          initialValues.tableName ||
          initialValues.TABLE_NAME ||
          '',
        display_name: initialValues.display_name || initialValues.DISPLAY_NAME || '',
        description: initialValues.description || initialValues.DESCRIPTION || '',
        sql_dialect: initialValues.sql_dialect || initialValues.SQL_DIALECT || '',
        tags: Array.isArray(initialValues.tags) ? initialValues.tags : [],
        tagInput: '',
        columns: mappedColumns,
        joins: mappedJoins,
        is_active: initialValues.is_active !== false && initialValues.IS_ACTIVE !== false,
        softDelete: {
          deleted: !!(initialValues?.soft_delete?.deleted ?? initialValues?.SOFT_DELETE?.DELETED),
          deleted_at:
            initialValues?.soft_delete?.deleted_at ||
            initialValues?.SOFT_DELETE?.DELETED_AT ||
            ''
        }
      });
      setColumnErrors(new Array(mappedColumns.length).fill(''));
      setActiveTab(0);
      setErrors({});
      return;
    }

    setFormValues({
      ...defaultFormValues,
      account_id: accountInfo?.id || ''
    });
    setColumnErrors(new Array(defaultFormValues.columns.length).fill(''));
    setErrors({});
    setActiveTab(0);
  }, [open, initialValues, accountInfo, connectionIndex, databaseOptions, resetAiJob]);

  const updateColumns = useCallback((updater) => {
    setFormValues((prev) => ({
      ...prev,
      columns: updater(prev.columns)
    }));
  }, []);

  const updateJoins = useCallback((updater) => {
    setFormValues((prev) => ({
      ...prev,
      joins: updater(prev.joins)
    }));
  }, []);

  const handleFieldChange = useCallback(
    (field) => (event) => {
      const value = event.target.value;
      setFormValues((prev) => ({ ...prev, [field]: value }));
    },
    []
  );

  const handleToggleActive = useCallback((_, checked) => {
    setFormValues((prev) => ({ ...prev, is_active: checked }));
  }, []);

  const handleConnectionChange = useCallback(
    (event) => {
      const value = event.target.value;
      const connection = value ? connectionIndex[value] : null;
      const nextDialect = mapConnectionTypeToDialect(connection?.connection_type || connection?.type);
      const nextCatalog =
        connection?.default_catalog ||
        connection?.catalog ||
        connection?.connection_catalog ||
        '';

      setFormValues((prev) => ({
        ...prev,
        connection_id: value,
        sql_dialect: nextDialect || prev.sql_dialect,
        catalog: nextCatalog || prev.catalog
      }));
    },
    [connectionIndex]
  );

  const handleTagInputChange = useCallback((event) => {
    const value = event.target.value;
    setFormValues((prev) => ({ ...prev, tagInput: value }));
  }, []);

  const handleAddTag = useCallback(() => {
    setFormValues((prev) => {
      const trimmed = prev.tagInput.trim();
      if (!trimmed || prev.tags.includes(trimmed)) {
        return prev;
      }
      return {
        ...prev,
        tags: [...prev.tags, trimmed],
        tagInput: ''
      };
    });
  }, []);

  const handleRemoveTag = useCallback((tag) => {
    setFormValues((prev) => ({
      ...prev,
      tags: prev.tags.filter((item) => item !== tag)
    }));
  }, []);

  const handleColumnFieldChange = useCallback((index, field, value) => {
    updateColumns((columns) =>
      columns.map((column, columnIndex) =>
        columnIndex === index ? { ...column, [field]: value } : column
      )
    );
  }, [updateColumns]);

  const handleColumnSwitchChange = useCallback((index, field, value) => {
    updateColumns((columns) =>
      columns.map((column, columnIndex) =>
        columnIndex === index ? { ...column, [field]: value } : column
      )
    );
  }, [updateColumns]);

  const handleAddColumn = useCallback(() => {
    updateColumns((columns) => [...columns, createDefaultColumn()]);
    setColumnErrors((prev) => [...prev, '']);
  }, [updateColumns]);

  const handleRemoveColumn = useCallback(
    (index) => {
      updateColumns((columns) => {
        const nextColumns = columns.filter((_, columnIndex) => columnIndex !== index);
        return nextColumns.length > 0 ? nextColumns : [createDefaultColumn()];
      });
      setColumnErrors((prev) => {
        const nextErrors = prev.filter((_, columnIndex) => columnIndex !== index);
        return nextErrors.length > 0 ? nextErrors : [''];
      });
    },
    [updateColumns]
  );

  const handleJoinFieldChange = useCallback((index, field, value) => {
    updateJoins((joins) =>
      joins.map((join, joinIndex) =>
        joinIndex === index ? { ...join, [field]: value } : join
      )
    );
  }, [updateJoins]);

  const handleAddJoin = useCallback(() => {
    updateJoins((joins) => [...joins, createDefaultJoin()]);
  }, [updateJoins]);

  const handleRemoveJoin = useCallback((index) => {
    updateJoins((joins) => {
      const nextJoins = joins.filter((_, joinIndex) => joinIndex !== index);
      return nextJoins.length > 0 ? nextJoins : [createDefaultJoin()];
    });
  }, [updateJoins]);

  const handleJoinConditionChange = useCallback((joinIndex, conditionIndex, field, value) => {
    updateJoins((joins) =>
      joins.map((join, idx) => {
        if (idx !== joinIndex) {
          return join;
        }
        const nextConditions = (join.on || []).map((condition, condIdx) =>
          condIdx === conditionIndex ? { ...condition, [field]: value } : condition
        );
        return {
          ...join,
          on: nextConditions
        };
      })
    );
  }, [updateJoins]);

  const handleAddJoinCondition = useCallback((joinIndex) => {
    updateJoins((joins) =>
      joins.map((join, idx) =>
        idx === joinIndex
          ? { ...join, on: [...(join.on || []), createDefaultJoinCondition()] }
          : join
      )
    );
  }, [updateJoins]);

  const handleRemoveJoinCondition = useCallback((joinIndex, conditionIndex) => {
    updateJoins((joins) =>
      joins.map((join, idx) => {
        if (idx !== joinIndex) {
          return join;
        }
        const nextConditions = (join.on || []).filter((_, condIdx) => condIdx !== conditionIndex);
        return {
          ...join,
          on: nextConditions.length > 0 ? nextConditions : [createDefaultJoinCondition()]
        };
      })
    );
  }, [updateJoins]);

  const handleSoftDeleteToggle = useCallback((_, checked) => {
    setFormValues((prev) => ({
      ...prev,
      softDelete: {
        ...prev.softDelete,
        deleted: checked,
        deleted_at: checked ? prev.softDelete.deleted_at : ''
      }
    }));
  }, []);

  const handleSoftDeleteDateChange = useCallback((event) => {
    const value = event.target.value;
    setFormValues((prev) => ({
      ...prev,
      softDelete: {
        ...prev.softDelete,
        deleted_at: value
      }
    }));
  }, []);

  const handleOpenImportWizard = useCallback(() => {
    setImportWizardOpen(true);
  }, []);

  const handleCloseImportWizard = useCallback(() => {
    setImportWizardOpen(false);
  }, []);

  // El wizard importa (crea/actualiza) las tablas elegidas directo, sin
  // pasar por este formulario de UNA sola entidad -- al terminar no queda
  // nada más que guardar acá, así que se cierra también este diálogo y se
  // avisa al padre (SemanticEntities.jsx) para que refresque su listado.
  const handleBulkImportComplete = useCallback(() => {
    setImportWizardOpen(false);
    onBulkImportComplete?.();
    onClose();
  }, [onBulkImportComplete, onClose]);

  /**
   * Corre el job de table_metadata_generator (modo "columns") con las
   * columnas que YA están en el formulario -- tipeadas a mano, importadas
   * de una conexión SQL, o las de una entidad existente que se está
   * editando. No necesita conexión en vivo, así que funciona igual para
   * entidades Excel/CSV (Athena/Glue). Devuelve el resultado o `null` si
   * falló la validación previa o el job (el error ya queda expuesto en
   * `aiError` para que el caller solo tenga que cortar el flujo).
   */
  const runMetadataGeneration = useCallback(async () => {
    const tableName = formValues.table_name.trim();
    if (!tableName) {
      reportAiError('Ingresá el nombre de la tabla antes de generar la descripción con IA');
      return null;
    }

    const columnsForAI = formValues.columns
      .filter((col) => col.column_name && col.column_name.trim())
      .map((col) => ({
        column_name: col.column_name.trim(),
        data_type: col.data_type?.trim() || 'text',
        is_primary_key: !!col.is_primary_key,
        nullable: col.nullable !== false
      }));

    if (columnsForAI.length === 0) {
      reportAiError('Agregá al menos una columna con nombre antes de generar la descripción con IA');
      return null;
    }

    const result = await runMetadataGenerationJob({
      table_name: tableName,
      columns: columnsForAI,
      semantic_database_id: formValues.semantic_database_id || undefined,
      generate_ai_descriptions: true,
      language: 'es'
    });
    // Sin esto, un fallo de Bedrock llegaba como "propuesta" igual a lo
    // cargado y se mostraba "La IA no propuso ningún cambio".
    if (result && aiGenerationFailed(result)) {
      reportAiError(AI_UNAVAILABLE_MESSAGE);
      return null;
    }
    return result;
  }, [formValues.table_name, formValues.columns, formValues.semantic_database_id, reportAiError, runMetadataGenerationJob]);

  /**
   * Click manual en "Generar con IA"/"Regenerar con IA". En creación se
   * aplica directo (solo rellena huecos, ver computeMergedFormValues) --
   * en edición abre el diálogo de override en vez de pisar nada (ver
   * MetadataOverridePreviewDialog más abajo).
   */
  const handleGenerateWithAIClick = useCallback(async () => {
    const metadata = await runMetadataGeneration();
    if (!metadata) {
      return;
    }

    if (mode === 'edit') {
      setAiProposal({
        metadata,
        currentTableFields: { display_name: formValues.display_name, description: formValues.description },
        currentColumns: formValues.columns
      });
    } else {
      setFormValues((prev) => computeMergedFormValues(prev, metadata));
    }
  }, [runMetadataGeneration, mode, formValues.display_name, formValues.description, formValues.columns]);

  const handleCloseAiProposal = useCallback(() => setAiProposal(null), []);

  const handleApplyAiProposal = useCallback((result) => {
    setFormValues((prev) => ({
      ...prev,
      ...(result.display_name !== undefined ? { display_name: result.display_name } : {}),
      ...(result.description !== undefined ? { description: result.description } : {}),
      columns: result.columns
    }));
    setColumnErrors((prev) => (prev.length === result.columns.length ? prev : new Array(result.columns.length).fill('')));
    setAiProposal(null);
  }, []);

  const validate = useCallback((values = formValues) => {
    const currentErrors = {};
    const nextColumnErrors = [];

    if (!values.semantic_database_id) {
      currentErrors.semantic_database_id = 'Selecciona una base семántica';
    }

    if (!resolvedAccountId) {
      currentErrors.account_id = 'No se pudo identificar la cuenta asociada';
    }

    if (!values.connection_id) {
      currentErrors.connection_id = 'Selecciona una conexión';
    }

    if (!values.database.trim()) {
      currentErrors.database = 'Ingresa el nombre de la base de datos';
    }

    if (!values.schema.trim()) {
      currentErrors.schema = 'Ingresa el esquema';
    }

    if (!values.table_name.trim()) {
      currentErrors.table_name = 'Ingresa el nombre físico de la tabla';
    }

    let hasValidColumn = false;
    values.columns.forEach((column, index) => {
      const name = column.column_name?.trim();
      if (!name) {
        nextColumnErrors[index] = 'La columna debe tener un nombre';
      } else {
        nextColumnErrors[index] = '';
        hasValidColumn = true;
      }
    });

    if (!hasValidColumn) {
      currentErrors.columns = 'Define al menos una columna con nombre';
    }

    setErrors(currentErrors);
    setColumnErrors(nextColumnErrors);

    return Object.keys(currentErrors).length === 0;
  }, [formValues, resolvedAccountId]);

  /**
   * En creación, si el usuario prendió "Generar los campos faltantes con
   * I.A." (autoFillWithAI, apagado por defecto -- decisión suya, no
   * automática) y quedaron huecos de negocio (tabla y/o columnas), dispara
   * el job de IA ANTES de guardar y rellena solo lo vacío (ver
   * computeMergedFormValues) -- un solo click para el usuario, sin
   * confirmación extra porque es contenido nuevo, no hay nada que
   * sobreescribir todavía. Si el switch está apagado, si ya completó todo
   * a mano, o si el job falla, sigue con lo que hay (el fallo ya quedó
   * visible en `aiError`, y `validate` abajo va a frenar el submit si de
   * verdad faltaba algo requerido).
   */
  const handleSubmit = useCallback(async () => {
    let valuesForSubmit = formValues;

    if (
      mode === 'create' &&
      autoFillWithAI &&
      hasMissingBusinessFields(formValues) &&
      canRunMetadataGeneration(formValues)
    ) {
      const metadata = await runMetadataGeneration();
      if (metadata) {
        valuesForSubmit = computeMergedFormValues(formValues, metadata);
        setFormValues(valuesForSubmit);
      }
    }

    if (!validate(valuesForSubmit)) {
      return;
    }

    onSubmit?.(buildSubmitPayload(valuesForSubmit, resolvedAccountId));
  }, [formValues, mode, autoFillWithAI, onSubmit, resolvedAccountId, runMetadataGeneration, validate]);

  return (
    <Dialog
      open={open}
      onClose={isSaving ? undefined : onClose}
      fullWidth
      maxWidth="lg"
      PaperProps={{
        sx: {
          bgcolor: 'var(--wb-bg-surface)',
          color: 'var(--wb-text-strong)',
          borderRadius: 0,
          border: '1px solid var(--wb-x-border-60a5fa26, rgba(96, 165, 250, 0.15))'
        }
      }}
    >
      <DialogTitle sx={{ borderBottom: '1px solid var(--wb-border)' }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', gap: 1 }}>
          <Typography variant="h6">
            {mode === 'edit' ? 'Editar entidad semántica' : 'Nueva entidad semántica'}
          </Typography>
          <Box sx={{ display: 'flex', gap: 1 }}>
            {mode === 'create' && (
              <Tooltip title={formValues.semantic_database_id ? '' : 'Elegí primero una base semántica arriba'}>
                <span>
                  <Button
                    variant="outlined"
                    onClick={handleOpenImportWizard}
                    disabled={isSaving || isGeneratingAI || !formValues.semantic_database_id}
                    sx={{
                      color: accentColor,
                      borderColor: accentColor,
                      '&:hover': {
                        borderColor: 'var(--wb-sky)',
                        bgcolor: 'var(--wb-x-bg-38bdf81a, rgba(56, 189, 248, 0.1))'
                      },
                      '&.Mui-disabled': {
                        color: 'var(--wb-accent-40)',
                        borderColor: 'var(--wb-x-border-38bdf833, rgba(56, 189, 248, 0.2))'
                      }
                    }}
                  >
                    Importar tablas
                  </Button>
                </span>
              </Tooltip>
            )}
            {mode === 'create' && (
              <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="botón-importar-tablas-solo-al-crear" sx={{ color: accentColor }} />
            )}
            <Button
              variant="outlined"
              startIcon={isGeneratingAI ? <CircularProgress size={16} sx={{ color: accentColor }} /> : <AutoAwesomeIcon />}
              onClick={handleGenerateWithAIClick}
              disabled={isSaving || isGeneratingAI}
              sx={{
                color: accentColor,
                borderColor: accentColor,
                '&:hover': {
                  borderColor: 'var(--wb-sky)',
                  bgcolor: 'var(--wb-x-bg-38bdf81a, rgba(56, 189, 248, 0.1))'
                }
              }}
            >
              {isGeneratingAI ? (aiProgressMessage || 'Generando…') : mode === 'edit' ? 'Regenerar con IA' : 'Generar con IA'}
            </Button>
            <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="botón-generar-con-ia--regenerar-con-ia" sx={{ color: accentColor }} />
          </Box>
        </Box>
      </DialogTitle>
      <DialogContent sx={{ pt: 3 }}>
        <Stack spacing={3}>
          {aiError && (
            <Alert severity="error" onClose={clearAiError}>
              {aiError}
            </Alert>
          )}
          {mode === 'create' && (
            <Box
              sx={{
                border: '1px solid var(--wb-x-border-38bdf833, rgba(56, 189, 248, 0.2))',
                bgcolor: 'var(--wb-x-bg-38bdf80d, rgba(56, 189, 248, 0.05))',
                p: 2
              }}
            >
              <Stack direction="row" spacing={0.5} alignItems="center">
              <FormControlLabel
                control={
                  <Switch
                    checked={autoFillWithAI}
                    onChange={(_, checked) => setAutoFillWithAI(checked)}
                    disabled={isGeneratingAI}
                    sx={switchSx}
                  />
                }
                label={<Typography sx={{ ...labelColor, fontWeight: 600 }}>Generar los campos faltantes con I.A.</Typography>}
              />
              <HelpIcon module="modelo-semantico" topic="tabla-semantica" anchor="generar-los-campos-faltantes-con-ia-solo-al-crear" />
              </Stack>
              <Typography variant="caption" sx={{ color: 'var(--wb-x-text-cbd5f5b2, rgba(203, 213, 245, 0.7))', display: 'block', ml: 5.5 }}>
                Al crear, completa con IA los campos de negocio que hayas dejado vacíos (tabla y columnas). Nunca pisa lo
                que ya cargaste a mano.
              </Typography>
            </Box>
          )}
          <MetadataSection
            formValues={formValues}
            errors={errors}
            databaseOptions={databaseOptions}
            connectionOptions={connectionOptions}
            resolvedAccountId={resolvedAccountId}
            accountName={accountName}
            selectedConnection={selectedConnection}
            labelColor={labelColor}
            accentColor={accentColor}
            onFieldChange={handleFieldChange}
            onConnectionChange={handleConnectionChange}
            onRefreshDatabases={onRefreshDatabases}
            onRefreshConnections={onRefreshConnections}
            isRefreshingDatabases={isRefreshingDatabases}
            isRefreshingConnections={isRefreshingConnections}
            onTagInputChange={handleTagInputChange}
            onAddTag={handleAddTag}
            onRemoveTag={handleRemoveTag}
            onToggleActive={handleToggleActive}
          />

          <Box
            sx={{
              bgcolor: 'var(--wb-x-bg-141b2d, #141b2d)',
              border: '1px solid var(--wb-x-border-f9731659, rgba(249, 115, 22, 0.35))',
              borderRadius: 0,
              p: 3
            }}
          >
            <Typography variant="subtitle1" sx={{ color: accentColor, fontWeight: 600, mb: 2 }}>
              Definición avanzada
            </Typography>

            <Tabs
              value={activeTab}
              onChange={(_, value) => setActiveTab(value)}
              textColor="inherit"
              sx={{
                '& .MuiTab-root': { color: 'var(--wb-text)' },
                '& .MuiTab-root.Mui-selected': { color: accentColor },
                '& .MuiTabs-indicator': { backgroundColor: accentColor }
              }}
            >
              <Tab label="Columnas" value={0} />
              <Tab label="Joins" value={1} />
            </Tabs>

            {activeTab === 0 && (
              <ColumnsTab
                columns={formValues.columns}
                columnErrors={columnErrors}
                labelColor={labelColor}
                switchSx={switchSx}
                accentColor={accentColor}
                onFieldChange={handleColumnFieldChange}
                onSwitchChange={handleColumnSwitchChange}
                onAddColumn={handleAddColumn}
                onRemoveColumn={handleRemoveColumn}
              />
            )}

            {activeTab === 1 && (
              <JoinsTab
                joins={formValues.joins}
                labelColor={labelColor}
                accentColor={accentColor}
                onFieldChange={handleJoinFieldChange}
                onAddJoin={handleAddJoin}
                onRemoveJoin={handleRemoveJoin}
                onAddCondition={handleAddJoinCondition}
                onRemoveCondition={handleRemoveJoinCondition}
                onConditionChange={handleJoinConditionChange}
              />
            )}
          </Box>

          <SoftDeleteSection
            formValues={formValues}
            labelColor={labelColor}
            switchSx={switchSx}
            onToggle={handleSoftDeleteToggle}
            onDateChange={handleSoftDeleteDateChange}
          />
        </Stack>
      </DialogContent>
      <DialogActions sx={{ borderTop: '1px solid var(--wb-border)', p: 2.5 }}>
        <Button onClick={onClose} disabled={isSaving} sx={{ color: 'var(--wb-text-label)' }}>
          Cancelar
        </Button>
        <Button
          variant="contained"
          onClick={handleSubmit}
          disabled={isSaving || isGeneratingAI}
          sx={{ bgcolor: accentColor, '&:hover': { bgcolor: 'var(--wb-x-bg-ea580c, #ea580c)' } }}
        >
          {isSaving
            ? 'Guardando…'
            : isGeneratingAI
              ? (aiProgressMessage || 'Completando con IA…')
              : mode === 'edit'
                ? 'Guardar cambios'
                : 'Crear entidad'}
        </Button>
      </DialogActions>

      {/* Wizard de importación masiva de tablas -- ver ImportTablesWizard */}
      <ImportTablesWizard
        open={importWizardOpen}
        onClose={handleCloseImportWizard}
        accountId={resolvedAccountId}
        semanticDatabaseId={formValues.semantic_database_id}
        onImportComplete={handleBulkImportComplete}
      />

      {/* Propuesta de IA en edición -- ver handleGenerateWithAIClick/handleApplyAiProposal */}
      <MetadataOverridePreviewDialog
        open={!!aiProposal}
        metadata={aiProposal?.metadata}
        currentTableFields={aiProposal?.currentTableFields}
        currentColumns={aiProposal?.currentColumns}
        onClose={handleCloseAiProposal}
        onApply={handleApplyAiProposal}
      />
    </Dialog>
  );
};

export default SemanticTableFormDialog;
